// Helper utilities for offline functionality

import { db } from '../db/database.js';

/**
 * Request persistent storage from the browser.
 * Prevents the browser from automatically clearing IndexedDB data
 * (including queued sync mutations) when storage is low.
 * Call this early in app startup.
 */
export async function requestPersistentStorage() {
  if (typeof navigator === 'undefined' || !navigator.storage) {
    console.log('[Storage] Storage API not available');
    return false;
  }

  try {
    // Check if already persisted
    if (navigator.storage.persisted) {
      const alreadyPersisted = await navigator.storage.persisted();
      if (alreadyPersisted) {
        console.log('[Storage] Persistent storage already granted');
        return true;
      }
    }

    // Request persistence
    if (navigator.storage.persist) {
      const granted = await navigator.storage.persist();
      if (granted) {
        console.log('[Storage] Persistent storage granted');
      } else {
        console.warn('[Storage] Persistent storage denied — browser may clear IndexedDB under storage pressure');
      }
      return granted;
    }

    console.warn('[Storage] navigator.storage.persist() not available');
    return false;
  } catch (error) {
    console.error('[Storage] Failed to request persistent storage:', error);
    return false;
  }
}

/**
 * Check if storage quota is exceeded
 */
export async function isStorageQuotaExceeded() {
  try {
    // Try to store a small test object
    const testData = { test: 'data', timestamp: Date.now() };
    await db.set('storage-test', testData);
    await db.delete('storage-test');
    return false;
  } catch (error) {
    if (error.name === 'QuotaExceededError' || error.code === 22) {
      return true;
    }
    return false;
  }
}

/**
 * Get estimated storage usage
 */
export async function getStorageUsage() {
  if (navigator.storage && navigator.storage.estimate) {
    try {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage,
        quota: estimate.quota,
        usagePercentage: (estimate.usage / estimate.quota) * 100,
      };
    } catch (error) {
      console.error('[OfflineHelpers] Failed to get storage estimate:', error);
    }
  }
  return null;
}

/**
 * Clear old data to free up space
 */
export async function cleanupOldData() {
  const stats = await db.getStats();
  console.log('[OfflineHelpers] Current stats:', stats);

  // Clean expired cache entries
  const repositories = [
    (await import('../db/repositories.js')).athletesRepository,
    (await import('../db/repositories.js')).trainersRepository,
    (await import('../db/repositories.js')).programsRepository,
    (await import('../db/repositories.js')).dietsRepository,
  ];

  let totalCleaned = 0;
  for (const repo of repositories) {
    const cleaned = await repo.cleanupExpired();
    totalCleaned += cleaned;
  }

  console.log('[OfflineHelpers] Cleaned', totalCleaned, 'expired entries');
  return totalCleaned;
}

/**
 * Handle corrupted cache gracefully
 */
export async function handleCorruptedCache(cacheKey) {
  console.warn('[OfflineHelpers] Handling corrupted cache:', cacheKey);
  
  try {
    await db.delete(cacheKey);
    return true;
  } catch (error) {
    console.error('[OfflineHelpers] Failed to delete corrupted cache:', error);
    return false;
  }
}

/**
 * Validate data structure
 */
export function validateDataStructure(data, schema) {
  if (!data || typeof data !== 'object') {
    return false;
  }

  for (const key of Object.keys(schema)) {
    if (!(key in data)) {
      return false;
    }

    const expectedType = schema[key];
    const actualType = typeof data[key];

    if (expectedType !== actualType && expectedType !== 'any') {
      return false;
    }
  }

  return true;
}

/**
 * Debounce function for performance
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function for performance
 */
export function throttle(func, limit) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Generate unique ID
 */
export function generateId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Format timestamp for display
 */
export function formatTimestamp(timestamp) {
  const date = new Date(timestamp);
  return date.toLocaleString();
}

/**
 * Calculate time difference
 */
export function timeDifference(timestamp) {
  const now = Date.now();
  const diff = now - timestamp;
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return `${seconds}s ago`;
}

/**
 * Merge arrays without duplicates
 */
export function mergeUniqueArrays(...arrays) {
  const merged = arrays.flat();
  const unique = new Set(merged);
  return Array.from(unique);
}

/**
 * Deep clone object
 */
export function deepClone(obj) {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (error) {
    console.error('[OfflineHelpers] Deep clone failed:', error);
    return obj;
  }
}

/**
 * Safe JSON parse
 */
export function safeJsonParse(str, defaultValue = null) {
  try {
    return JSON.parse(str);
  } catch (error) {
    console.error('[OfflineHelpers] JSON parse failed:', error);
    return defaultValue;
  }
}

/**
 * Safe JSON stringify
 */
export function safeJsonStringify(obj, defaultValue = '{}') {
  try {
    return JSON.stringify(obj);
  } catch (error) {
    console.error('[OfflineHelpers] JSON stringify failed:', error);
    return defaultValue;
  }
}
